import { useCatalog } from "catalog/Catalog.context";
import {
  CatalogLink,
  CatalogAutoplayButton,
  CatalogPagingButton,
} from "catalog/CatalogNavigations";
import { Image, Br, Anime } from "components/elements";
import { twMerge } from "tailwind-merge";
import {
  SVG_Hamburger,
  SVG_LogoNCat,
  SVG_Pause,
  SVG_Play,
  SVG_PrevArrow,
  SVG_NextArrow,
} from "./SVG";
import { useLocation, useParams, useNavigate } from "react-router-dom";

export default function GlobalNavigation() {
  const { activePageInfo, isFirstPage, isLastPage } = useCatalog();

  if (!activePageInfo) return null;

  const { depth1, depth2, header } = activePageInfo;

  return (
    <div className="h-full w-full">
      <Header />
      <Controls />
      {depth2 !== "intro" && (
        <span
          className={twMerge(
            "leadig-[1] absolute left-0 top-1/2 z-[500] -translate-x-[20%] -translate-y-1/2 font-second text-[80px] font-light text-[#6E6E6E] opacity-30 [writing-mode:vertical-rl]",
            header?.controlTheme === "white" && "text-white",
            "mo:hidden",
          )}
        >
          <Anime anime="fadeLeft">
            {
              { company: "Company", brand: "BRAND N.CAT", product: "Product", store: "Solution" }[
                depth1
              ]
            }
          </Anime>
        </span>
      )}
    </div>
  );
}

function Header() {
  const { isUSA } = useCatalog();
  const { lang } = useParams();
  const { activePageInfo, isResponsive } = useCatalog();

  if (!activePageInfo) return null;

  const { header } = activePageInfo;

  return (
    <header
      className={twMerge(
        "absolute left-0 top-0 z-[9999] flex h-[128px] w-full items-center justify-between gap-[30px] px-[45px]",
        "mo:fixed mo:h-[60px] mo:gap-[15px] mo:border-b mo:border-b-[#dfdfdf] mo:bg-white mo:px-[15px]",
        header?.boxed
          ? "border-b-1 border-b border-b-[#C0C0C0] bg-white"
          : "border-0 bg-transparent",
      )}
    >
      <h1
        className={twMerge(
          "transition-transform duration-[600ms]",
          "mo:!translate-y-0",
          header?.logo ? "translate-y-0" : "translate-y-[-150px]",
        )}
      >
        <CatalogLink to="cover">
          <SVG_LogoNCat
            theme={isResponsive ? "#6E6E6E" : header?.theme === "white" ? "white" : "#6E6E6E"}
            className="mo:h-[24px] mo:w-auto"
          />
        </CatalogLink>
      </h1>
      <nav
        className={twMerge(
          "ml-auto transition-transform duration-[600ms]",
          "mo:hidden",
          header?.gnb ? "translate-y-0" : "translate-y-[-150px]",
        )}
      >
        <GNB
          menu={
            lang === "en_sbm"
              ? [
                  { title: "Company", depth1: "company", to: "company-intro" },
                  { title: "Brand N.CAT", depth1: "brand", to: "brand-intro" },
                  { title: "Product", depth1: "product", to: "product/intro" },
                  { title: "Solution", depth1: "store", to: "store-intro" },
                ]
              : [
                  { title: "Company", depth1: "company", to: "company-intro" },
                  { title: "Brand N.CAT", depth1: "brand", to: "brand-intro" },
                  { title: "Solution", depth1: "store", to: "store-intro" },
                  { title: "Product", depth1: "product", to: "product/intro" },
                ]
          }
        />
      </nav>
      <nav
        className={twMerge(
          "flex items-center gap-[24px]",
          "transition-transform duration-[600ms]",
          "mo:!translate-y-0 mo:gap-[12px]",
          header?.controls ? "translate-y-0" : "translate-y-[-150px]",
        )}
      >
        {!isUSA && (
          <div
            className={twMerge(
              "flex items-center gap-2 rounded-full border border-white px-3 text-white transition-colors",
              header?.theme !== "white" && "border-[#6E6E6E] text-[#6E6E6E]",
              "mo:border-[#6E6E6E] mo:text-[#6E6E6E]",
            )}
          >
            <LangButton langTo="ko">KOR</LangButton>
            <LangButton langTo="en">ENG</LangButton>
          </div>
        )}
        <CatalogAutoplayButton
          render={(autoplayState) => (
            <>
              {autoplayState ? (
                <SVG_Pause
                  theme={isResponsive ? "#6E6E6E" : header?.theme === "white" ? "white" : "#6E6E6E"}
                  className="mo:h-auto mo:w-[32px]"
                />
              ) : (
                <SVG_Play
                  theme={isResponsive ? "#6E6E6E" : header?.theme === "white" ? "white" : "#6E6E6E"}
                  className="mo:h-auto mo:w-[32px]"
                />
              )}
            </>
          )}
          className="btn"
        />
        <CatalogLink to="catalog-index">
          <SVG_Hamburger
            theme={isResponsive ? "#6E6E6E" : header?.theme === "white" ? "white" : "#6E6E6E"}
            className="mo:h-auto mo:w-[29px]"
          />
        </CatalogLink>
      </nav>
    </header>
  );
}

function GNB({ menu = [] }) {
  const { activePageInfo } = useCatalog();

  if (!activePageInfo) return null;

  const { depth1, header } = activePageInfo;

  return (
    <ul className={twMerge("itens-center flex")}>
      {menu.map((item, i) => (
        <li
          key={`gnb-depth1-${i}`}
          className={twMerge(
            "relative after:absolute after:right-0 after:top-1/2 after:block after:h-[0.8em] after:w-[2px] after:-translate-y-1/2 after:translate-x-1/2 after:bg-[#ADADAD] last:after:hidden",
            header?.theme === "white" ? "after:bg-white" : "after:bg-[#ADADAD]",
          )}
        >
          <CatalogLink
            to={item.to}
            className={twMerge(
              "px-[1em] font-second text-[19px] uppercase transition-colors",
              header?.theme === "white" ? "text-white" : "text-[#313131]",
              item.depth1 === depth1 && "font-bold",
            )}
          >
            {item.title}
          </CatalogLink>
        </li>
      ))}
    </ul>
  );
}

function LangButton({ children, langTo, className }) {
  const { lang } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleClick = () => {
    const newPath = pathname.replace(lang, langTo);
    navigate(newPath);
  };

  return (
    <button
      onClick={handleClick}
      className={twMerge(
        "px-1 py-1.5 hover:opacity-80 mo:py-0.5",
        lang === langTo && "font-bold",
        className,
      )}
    >
      {children}
    </button>
  );
}

function Controls() {
  const { activePageInfo, isFirstPage, isLastPage, isResponsive } = useCatalog();

  if (!activePageInfo) return null;

  const { header } = activePageInfo;

  return (
    <nav
      className={twMerge(
        "pointer-events-none absolute bottom-0 left-0 z-[9999] flex w-full justify-between px-[30px] py-[24px] *:pointer-events-auto",
        "mo:fixed mo:border-t mo:bg-white mo:px-[0px] mo:py-0",
      )}
    >
      <CatalogPagingButton
        direction="prev"
        className={twMerge(
          "flex h-[56px] w-[56px] items-center justify-center",
          "mo:h-[50px] mo:w-[50px]",
          isFirstPage && "pointer-events-none opacity-0",
        )}
      >
        <SVG_PrevArrow
          theme={
            isResponsive
              ? "#6E6E6E"
              : header?.theme === "white" || header?.controlTheme === "white"
                ? "white"
                : "#707070"
          }
          className="mo:h-[20px] mo:w-[20px]"
        />
      </CatalogPagingButton>
      <CatalogPagingButton
        direction="next"
        className={twMerge(
          "flex h-[56px] w-[56px] items-center justify-center",
          "mo:h-[50px] mo:w-[50px]",
          isLastPage && "pointer-events-none opacity-0",
        )}
      >
        <SVG_NextArrow
          theme={
            isResponsive
              ? "#6E6E6E"
              : header?.theme === "white" || header?.controlTheme === "white"
                ? "white"
                : "#707070"
          }
          className="mo:h-[20px] mo:w-[20px]"
        />
      </CatalogPagingButton>
    </nav>
  );
}
